import { ArtistTaxInfoAttachmentInit, ArtistTaxInfoFileType, ArtistTaxInfoProperty, NationalityType } from 'src/constant/artistTaxInfoConstants';
import { ArtistTaxInfoAttachmentModel } from 'src/models/TaxCenterModel';
import { Button, Col, Form, Input, Row, Upload, UploadProps, message } from 'antd';
import Title from 'antd/es/typography/Title';
import { useEffect, useState } from 'react';
import { StringConstants } from 'src/constant/stringConstants';
import { UploadOutlined } from '@ant-design/icons';
import { UploadFileConstants, UploadListType, UploadValidationMessages } from 'src/constant/uploadFileConstants';
import { useSessionContext } from 'src/context/session-context';
import TranslationKey from 'src/i18n/translation';
import { addFileToS3 } from 'src/helper/s3UploadHelper';
import { RegexPattern } from 'src/constant/constants';

interface CompanyTaxCenterProps {
  nationalityType: string,
  compVatRegisterCertificateAttachment: ArtistTaxInfoAttachmentModel,
  compCerRegIssueAttachment: ArtistTaxInfoAttachmentModel,
  compCommercialRegAttachment: ArtistTaxInfoAttachmentModel,
  compListOfShareholdersAttachment: ArtistTaxInfoAttachmentModel,
  compIdCardAuthorizedPersonAttachment: ArtistTaxInfoAttachmentModel,
  onUploadFileChanged: (
    fileType: ArtistTaxInfoFileType,
    file: ArtistTaxInfoAttachmentModel) => void;
}

export default function CompanyTaxCenter(props: CompanyTaxCenterProps) {
  const [, setIsThai] = useState(false);
  const { userProfile } = useSessionContext();
  const [compVatRegisterCertificateFile, setCompVatRegisterCertificateFile] = useState<ArtistTaxInfoAttachmentModel>(ArtistTaxInfoAttachmentInit.defaultFileValue);
  const [compCerRegIssueFile, setCompCerRegIssueFile] = useState<ArtistTaxInfoAttachmentModel>(ArtistTaxInfoAttachmentInit.defaultFileValue);
  const [compCommercialRegFile, setCompCommercialRegFile] = useState<ArtistTaxInfoAttachmentModel>(ArtistTaxInfoAttachmentInit.defaultFileValue);
  const [compListOfShareholdersFile, setCompListOfShareholdersFile] = useState<ArtistTaxInfoAttachmentModel>(ArtistTaxInfoAttachmentInit.defaultFileValue);
  const [compIdCardAuthorizedPersonFile, setCompIdCardAuthorizedPersonFile] = useState<ArtistTaxInfoAttachmentModel>(ArtistTaxInfoAttachmentInit.defaultFileValue);

  useEffect(() => {
    if (props.nationalityType) {
      setIsThai(props.nationalityType === NationalityType.Thai);
    }
  }, [props.nationalityType]);

  useEffect(() => {
    if (props.compVatRegisterCertificateAttachment) {
      setCompVatRegisterCertificateFile(props.compVatRegisterCertificateAttachment);
    }
  }, [props.compVatRegisterCertificateAttachment]);

  useEffect(() => {
    if (props.compCerRegIssueAttachment) {
      setCompCerRegIssueFile(props.compCerRegIssueAttachment);
    }
  }, [props.compCerRegIssueAttachment]);

  useEffect(() => {
    if (props.compCommercialRegAttachment) {
      setCompCommercialRegFile(props.compCommercialRegAttachment);
    }
  }, [props.compCommercialRegAttachment]);

  useEffect(() => {
    if (props.compListOfShareholdersAttachment) {
      setCompListOfShareholdersFile(props.compListOfShareholdersAttachment);
    }
  }, [props.compListOfShareholdersAttachment]);

  useEffect(() => {
    if (props.compIdCardAuthorizedPersonAttachment) {
      setCompIdCardAuthorizedPersonFile(props.compIdCardAuthorizedPersonAttachment);
    }
  }, [props.compIdCardAuthorizedPersonAttachment]);

  const deleteFileFromS3 = async (fileName: string) => {
    // TODO: call backend to delete file from S3

    // const params = {
    //   Bucket: S3Constants.BUCKET,
    //   Key: fileName,
    // };

    // await s3Bucket.deleteObject(params, (err) => {
    //   if (err) {
    //     console.log(err, err.stack);
    //     message.error(`${fileName} file remove failed.`);
    //   }
    // });
  };

  const validateUploadFile = async (file: File, onError: any) => {
    const unknowFileType = !file.type || file.type === StringConstants.EMPTY;
    const fileTypeNotSupport = !UploadFileConstants.MIME_RELEASES_ACCEPT.includes(file.type);

    if (unknowFileType || fileTypeNotSupport) {
      const errorMessage = file.name + StringConstants.SPACE + UploadValidationMessages.INVALID_FILE_TYPE;
      onError(errorMessage);

      return errorMessage;
    }

    return StringConstants.EMPTY.toString();
  };

  const createUploadPropsForTaxInfoFile = (
    fileType: ArtistTaxInfoFileType,
    fileObject: { fileName: string }
  ): UploadProps => ({
    multiple: false,
    listType: UploadListType.TEXT,
    accept: UploadFileConstants.MIME_RELEASES_ACCEPT.toString(),
    maxCount: 1,
    showUploadList: true,
    async onRemove() {
      deleteFileFromS3(fileObject.fileName);
    },
    async customRequest({ file, onError, onSuccess, onProgress }) {
      const originalFile = file as File;
      const errorMessage = await validateUploadFile(originalFile, onError);

      if (errorMessage !== StringConstants.EMPTY) {
        message.error(errorMessage, 10);
        return;
      }

      switch (fileType) {
        case ArtistTaxInfoFileType.compVatRegisterCertificate: {
          setCompVatRegisterCertificateFile(ArtistTaxInfoAttachmentInit.defaultFileValue);
          break;
        }
        case ArtistTaxInfoFileType.compCerRegIssue: {
          setCompCerRegIssueFile(ArtistTaxInfoAttachmentInit.defaultFileValue);
          break;
        }
        case ArtistTaxInfoFileType.compCommercialReg: {
          setCompCommercialRegFile(ArtistTaxInfoAttachmentInit.defaultFileValue);
          break;
        }
        case ArtistTaxInfoFileType.compListOfShareholders: {
          setCompListOfShareholdersFile(ArtistTaxInfoAttachmentInit.defaultFileValue);
          break;
        }
        case ArtistTaxInfoFileType.compIdCardAuthorizedPerson: {
          setCompIdCardAuthorizedPersonFile(ArtistTaxInfoAttachmentInit.defaultFileValue);
          break;
        }
      }

      try {
        const fileKey = await addFileToS3(
          fileType.toString(),
          originalFile,
          onProgress);

        onSuccess?.(fileKey);

        var fileUploaded = {
          url: `${process.env.REACT_APP_S3_BUCKET}${fileKey}`,
          fileNameOriginal: originalFile.name,
          fileName: fileKey
        };

        props.onUploadFileChanged(fileType, fileUploaded);

        switch (fileType) {
          case ArtistTaxInfoFileType.compVatRegisterCertificate: {
            setCompVatRegisterCertificateFile(fileUploaded);
            break;
          }
          case ArtistTaxInfoFileType.compCerRegIssue: {
            setCompCerRegIssueFile(fileUploaded);
            break;
          }
          case ArtistTaxInfoFileType.compCommercialReg: {
            setCompCommercialRegFile(fileUploaded);
            break;
          }
          case ArtistTaxInfoFileType.compListOfShareholders: {
            setCompListOfShareholdersFile(fileUploaded);
            break;
          }
          case ArtistTaxInfoFileType.compIdCardAuthorizedPerson: {
            setCompIdCardAuthorizedPersonFile(fileUploaded);
            break;
          }
        }
      } catch (err: any) {
        onError?.(err);
      }
    }
  });

  const uploadPropsCompVatRegisterCertificate =
    createUploadPropsForTaxInfoFile(
      ArtistTaxInfoFileType.compVatRegisterCertificate,
      compVatRegisterCertificateFile);

  const uploadPropsCompCerRegIssue =
    createUploadPropsForTaxInfoFile(
      ArtistTaxInfoFileType.compCerRegIssue,
      compCerRegIssueFile);

  const uploadPropsCompCommercialReg =
    createUploadPropsForTaxInfoFile(
      ArtistTaxInfoFileType.compCommercialReg,
      compCommercialRegFile);

  const uploadPropsCompListOfShareholders =
    createUploadPropsForTaxInfoFile(
      ArtistTaxInfoFileType.compListOfShareholders,
      compListOfShareholdersFile);

  const uploadPropsCompIdCardAuthorizedPerson =
    createUploadPropsForTaxInfoFile(
      ArtistTaxInfoFileType.compIdCardAuthorizedPerson,
      compIdCardAuthorizedPersonFile);

  return (
    <>
      <Row>
        <Col span={24} className='mb-4'>
          <Title level={2}>{TranslationKey('taxCenter.pleaseComplete')}</Title>
          <p>{TranslationKey('taxCenter.basedOnTheInformation')}</p>
          <p>{TranslationKey('taxCenter.youMayWantToReview')}</p>
          <p><span className='text-danger'>*</span> {TranslationKey('taxCenter.markedFields')}</p>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Title level={3}>
            {TranslationKey('taxCenter.part1')}: {TranslationKey('taxCenter.identificationOfBeneficial')}
          </Title>
          <p>{TranslationKey('taxCenter.nameAs')}<span className='text-danger'>*</span></p>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={`${TranslationKey('taxCenter.nameOfJuristicPerson')} (${TranslationKey('inputLanguage.th')})`}
            name="juristicPersonNameTh"
            rules={[
              {
                required: true,
                message: `${TranslationKey('taxCenter.nameOfJuristicPerson')} (${TranslationKey('inputLanguage.th')})`,
              }]}>
            <Input
              size='large'
              className='w-100'
            />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={`${TranslationKey('taxCenter.nameOfJuristicPerson')} (${TranslationKey('inputLanguage.en')})`}
            name="juristicPersonNameEn"
            rules={[
              {
                required: true,
                message: `${TranslationKey('taxCenter.nameOfJuristicPerson')} (${TranslationKey('inputLanguage.en')})`,
              }]}>
            <Input
              size='large'
              className='w-100'
            />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={TranslationKey('taxCenter.idCardNumber')}
            name="taxIdNumber"
            rules={[
              {
                required: true,
                message: TranslationKey('taxCenter.requireIdCardNumber'),
              },
              {
                pattern: RegexPattern.number,
                message: TranslationKey('taxCenter.requireNumberOnly')
              },]}>
            <Input
              size='large'
              className='w-100'
              maxLength={13}
              onKeyDown={e => {
                if (
                  !RegexPattern.number.test(e.key) &&
                  !['Backspace'].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              onPaste={e => {
                const paste = e.clipboardData.getData('text');

                if (!RegexPattern.number.test(paste)) {
                  e.preventDefault();
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Title level={3}>{TranslationKey('taxCenter.juristicPersonAddress')} ({TranslationKey('inputLanguage.th')})</Title>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={TranslationKey('taxCenter.addressLine1')}
            name="companyAddress1Th">
            <Input
              size='large'
              className='w-100'
            />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={TranslationKey('taxCenter.addressLine2')}
            name="companyAddress2Th">
            <Input
              size='large'
              className='w-100'
            />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={TranslationKey('taxCenter.city')}
            name="cityTh">
            <Input
              size='large'
              className='w-100'
            />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={TranslationKey('taxCenter.postal')}
            name="postcodeTh"
            rules={[
              {
                pattern: RegexPattern.number,
                message: TranslationKey('taxCenter.requireNumberOnly')
              },
            ]}>
            <Input
              size='large'
              className='w-100'
              maxLength={10}
              onKeyDown={e => {
                if (
                  !RegexPattern.number.test(e.key) &&
                  !['Backspace'].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              onPaste={e => {
                const paste = e.clipboardData.getData('text');

                if (!RegexPattern.number.test(paste)) {
                  e.preventDefault();
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Title level={3}>
            {TranslationKey('taxCenter.juristicPersonAddress')} ({TranslationKey('inputLanguage.en')})
          </Title>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={TranslationKey('taxCenter.address1')}
            name="companyAddress1En">
            <Input
              size='large'
              className='w-100'
            />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={TranslationKey('taxCenter.address2')}
            name="companyAddress2En">
            <Input
              size='large'
              className='w-100'
            />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={TranslationKey('taxCenter.city')}
            name="cityEn">
            <Input
              size='large'
              className='w-100'
            />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={TranslationKey('taxCenter.postal')}
            name="postcodeEn"
            rules={[
              {
                pattern: RegexPattern.number,
                message: TranslationKey('taxCenter.requireNumberOnly')
              },
            ]}>
            <Input
              size='large'
              className='w-100'
              maxLength={10}
              onKeyDown={e => {
                if (
                  !RegexPattern.number.test(e.key) &&
                  !['Backspace'].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              onPaste={e => {
                const paste = e.clipboardData.getData('text');

                if (!RegexPattern.number.test(paste)) {
                  e.preventDefault();
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={TranslationKey('taxCenter.companyContactEmail')}
            name="companyContactEmail">
            <Input
              size='large'
              className='w-100'
            />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item
            label={TranslationKey('taxCenter.companyContactPhone')}
            name="companyContactPhone">
            <Input
              size='large'
              className='w-100'
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Title level={3}>
            {TranslationKey('taxCenter.upload5TaxFiles')}
          </Title>
          <Form.Item
            name={ArtistTaxInfoProperty.compVatRegisterCertificateAttachmentUrl}
            className='mt-4 text-light '
            label={TranslationKey('taxCenter.taxFiles.1')}>
            <div className='upload-form-box'>
              {
                compVatRegisterCertificateFile && compVatRegisterCertificateFile.url !== StringConstants.EMPTY
                  ?
                  <div className='d-flex gap-2'>
                    <Button type='default' size='small'
                      onClick={() => {
                        setCompVatRegisterCertificateFile(ArtistTaxInfoAttachmentInit.defaultFileValue);
                        props.onUploadFileChanged(
                          ArtistTaxInfoFileType.compVatRegisterCertificate,
                          ArtistTaxInfoAttachmentInit.defaultFileValue)
                      }}
                      danger>
                      {TranslationKey('button.newUpload')}
                    </Button>
                    <a href={compVatRegisterCertificateFile.url}
                      target='_blank'
                      rel="noreferrer">
                      <span style={{ color: 'black' }}>
                        {compVatRegisterCertificateFile.fileNameOriginal}
                      </span>
                    </a>
                  </div>
                  :
                  <Upload {...uploadPropsCompVatRegisterCertificate}>
                    <Button icon={<UploadOutlined />}>
                      {TranslationKey('button.clickToUpload')}
                    </Button>
                  </Upload>
              }
            </div>
          </Form.Item>
          <Form.Item name={ArtistTaxInfoProperty.compVatRegisterCertificateAttachmentFileName} hidden></Form.Item>
          <Form.Item name={ArtistTaxInfoProperty.compVatRegisterCertificateAttachmentFileNameOriginal} hidden></Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name={ArtistTaxInfoProperty.compCerRegIssueAttachmentUrl}
            className='mt-4 text-light '
            label={TranslationKey('taxCenter.taxFiles.2')}>
            <div className='upload-form-box'>
              {
                compCerRegIssueFile && compCerRegIssueFile.url !== StringConstants.EMPTY
                  ?
                  <div className='d-flex gap-2'>
                    <Button type='default' size='small'
                      onClick={() => {
                        setCompCerRegIssueFile(ArtistTaxInfoAttachmentInit.defaultFileValue);
                        props.onUploadFileChanged(
                          ArtistTaxInfoFileType.compCerRegIssue,
                          ArtistTaxInfoAttachmentInit.defaultFileValue)
                      }}
                      danger>
                      {TranslationKey('button.newUpload')}
                    </Button>
                    <a href={compCerRegIssueFile.url} target='_blank' rel="noreferrer">
                      <span style={{ color: 'black' }}>{compCerRegIssueFile.fileNameOriginal}</span>
                    </a>
                  </div>
                  :
                  <Upload {...uploadPropsCompCerRegIssue}>
                    <Button icon={<UploadOutlined />}>{TranslationKey('button.clickToUpload')}</Button>
                  </Upload>
              }
            </div>
          </Form.Item>
          <Form.Item name={ArtistTaxInfoProperty.compCerRegIssueAttachmentFileName} hidden></Form.Item>
          <Form.Item name={ArtistTaxInfoProperty.compCerRegIssueAttachmentFileNameOriginal} hidden></Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name={ArtistTaxInfoProperty.compCommercialRegAttachmentUrl}
            className='mt-4 text-light '
            label={TranslationKey('taxCenter.taxFiles.3')}>
            <div className='upload-form-box'>
              {
                compCommercialRegFile && compCommercialRegFile.url !== StringConstants.EMPTY
                  ?
                  <div className='d-flex gap-2'>
                    <Button type='default' size='small'
                      onClick={() => {
                        setCompCommercialRegFile(ArtistTaxInfoAttachmentInit.defaultFileValue);
                        props.onUploadFileChanged(
                          ArtistTaxInfoFileType.compCommercialReg,
                          ArtistTaxInfoAttachmentInit.defaultFileValue)
                      }}
                      danger>
                      {TranslationKey('button.newUpload')}
                    </Button>
                    <a href={compCommercialRegFile.url} target='_blank' rel="noreferrer">
                      <span style={{ color: 'black' }}>{compCommercialRegFile.fileNameOriginal}</span>
                    </a>
                  </div>
                  :
                  <Upload {...uploadPropsCompCommercialReg}>
                    <Button icon={<UploadOutlined />}>{TranslationKey('button.clickToUpload')}</Button>
                  </Upload>
              }
            </div>
          </Form.Item>
          <Form.Item name={ArtistTaxInfoProperty.compCommercialRegAttachmentFileName} hidden></Form.Item>
          <Form.Item name={ArtistTaxInfoProperty.compCommercialRegAttachmentFileNameOriginal} hidden></Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name={ArtistTaxInfoProperty.compListOfShareholdersAttachmentUrl}
            className='mt-4 text-light '
            label={TranslationKey('taxCenter.taxFiles.4')}>
            <div className='upload-form-box'>
              {
                compListOfShareholdersFile && compListOfShareholdersFile.url !== StringConstants.EMPTY
                  ?
                  <div className='d-flex gap-2'>
                    <Button type='default' size='small'
                      onClick={() => {
                        setCompListOfShareholdersFile(ArtistTaxInfoAttachmentInit.defaultFileValue);
                        props.onUploadFileChanged(
                          ArtistTaxInfoFileType.compListOfShareholders,
                          ArtistTaxInfoAttachmentInit.defaultFileValue)
                      }}
                      danger>
                      {TranslationKey('button.newUpload')}
                    </Button>
                    <a href={compListOfShareholdersFile.url} target='_blank' rel="noreferrer">
                      <span style={{ color: 'black' }}>{compListOfShareholdersFile.fileNameOriginal}</span>
                    </a>
                  </div>
                  :
                  <Upload {...uploadPropsCompListOfShareholders}>
                    <Button icon={<UploadOutlined />}>{TranslationKey('button.clickToUpload')}</Button>
                  </Upload>
              }
            </div>
          </Form.Item>
          <Form.Item name={ArtistTaxInfoProperty.compListOfShareholdersAttachmentFileName} hidden></Form.Item>
          <Form.Item name={ArtistTaxInfoProperty.compListOfShareholdersAttachmentFileNameOriginal} hidden></Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name={ArtistTaxInfoProperty.compIdCardAuthorizedPersonAttachmentUrl}
            className='mt-4 text-light '
            label={TranslationKey('taxCenter.taxFiles.5')}>
            <div className='upload-form-box'>
              {
                compIdCardAuthorizedPersonFile && compIdCardAuthorizedPersonFile.url !== StringConstants.EMPTY
                  ?
                  <div className='d-flex gap-2'>
                    <Button type='default' size='small'
                      onClick={() => {
                        setCompIdCardAuthorizedPersonFile(ArtistTaxInfoAttachmentInit.defaultFileValue);
                        props.onUploadFileChanged(
                          ArtistTaxInfoFileType.compIdCardAuthorizedPerson,
                          ArtistTaxInfoAttachmentInit.defaultFileValue)
                      }}
                      danger>
                      {TranslationKey('button.newUpload')}
                    </Button>
                    <a href={compIdCardAuthorizedPersonFile.url} target='_blank' rel="noreferrer">
                      <span style={{ color: 'black' }}>{compIdCardAuthorizedPersonFile.fileNameOriginal}</span>
                    </a>
                  </div>
                  :
                  <Upload {...uploadPropsCompIdCardAuthorizedPerson}>
                    <Button icon={<UploadOutlined />}>{TranslationKey('button.clickToUpload')}</Button>
                  </Upload>
              }
            </div>
          </Form.Item>
          <Form.Item name={ArtistTaxInfoProperty.compIdCardAuthorizedPersonAttachmentFileName} hidden></Form.Item>
          <Form.Item name={ArtistTaxInfoProperty.compIdCardAuthorizedPersonAttachmentFileNameOriginal} hidden></Form.Item>
        </Col>
      </Row>
    </>
  );
}
